import { useCallback } from 'react';
import {
  Participation,
  ParticipationReportReason,
  ParticipationStatus,
  StudyType,
  useAcceptParticipation,
  useCreateDefaultEvaluationParticipation,
  useDeclineParticipation,
  useMarkAsParticipated,
  usePayParticipant,
  useReportParticipant,
  useUndoDeclineParticipation,
} from '@app/api';
import { useRequestBillingInformation, useToast } from 'components';
import { focusManager } from 'react-query';
import { ApiFormattedException } from '../store/modules/exceptions';

export const ParticipationDragTypes: {
  [status in ParticipationStatus]: string;
} = {
  invited: 'PARTICIPATION_INVITED',
  applied: 'PARTICIPATION_APPLIED',
  accepted: 'PARTICIPATION_ACCEPTED',
  participated: 'PARTICIPATION_PARTICIPATED',
  booked: 'PARTICIPATION_BOOKED',
  payment_requested: 'PARTICIPATION_PAYMENT_REQUESTED',
  processing_payment: 'PARTICIPATION_PROCESSING_PAYMENT',
  payed: 'PARTICIPATION_PAYED',
  declined: 'PARTICIPATION_DECLINED',
  in_progress: 'PARTICIPATION_IN_PROGRESS',
  canceled: 'PARTICIPATION_CANCELED',
  out_of_quota: 'PARTICIPATION_OUT_OF_QUOTA',
  noshow: 'PARTICIPATION_NOSHOW',
  [ParticipationStatus.NOT_INTERESTED]: 'PARTICIPATION_NOT_INTERESTED',
};

export const getDragType = (p: Participation) => {
  return getDragTypeFromStatus(p.status);
};
export const getDragTypeFromStatus = (status: ParticipationStatus) => {
  return ParticipationDragTypes[status];
};

export const DropAcceptTypes: {
  [status: string]: string[];
} = {
  invited: [ParticipationDragTypes.invited],
  applied: [
    ParticipationDragTypes.invited,
    ParticipationDragTypes.applied,
    ParticipationDragTypes.declined,
    ParticipationDragTypes.noshow,
  ],
  accepted_moderated: [
    ParticipationDragTypes.accepted,
    ParticipationDragTypes.invited,
    ParticipationDragTypes.applied,
    ParticipationDragTypes.declined,
    ParticipationDragTypes.out_of_quota,
  ],
  accepted: [
    ParticipationDragTypes.accepted,
    ParticipationDragTypes.invited,
    ParticipationDragTypes.applied,
    ParticipationDragTypes.declined,
    ParticipationDragTypes.participated,
  ],
  participated_moderated: [
    ParticipationDragTypes.participated,
    ParticipationDragTypes.payment_requested,
  ],
  participated: [
    ParticipationDragTypes.participated,
    ParticipationDragTypes.payment_requested,
    ParticipationDragTypes.accepted,
    ParticipationDragTypes.in_progress,
  ],
  payment_requested: [
    ParticipationDragTypes.payment_requested,
    ParticipationDragTypes.participated,
  ],
  processing_payment: [ParticipationDragTypes.processing_payment],
  payed: [ParticipationDragTypes.payed],
  canceled: [ParticipationDragTypes.canceled, ParticipationDragTypes.booked],
  declined: [
    ParticipationDragTypes.declined,
    ParticipationDragTypes.invited,
    ParticipationDragTypes.applied,
    ParticipationDragTypes.accepted,
    ParticipationDragTypes.out_of_quota,
  ],
  out_of_quota: [ParticipationDragTypes.out_of_quota],
  noshow: [ParticipationDragTypes.noshow],
};

const getDropAcceptTypesForSingleStatus = (
  status: ParticipationStatus,
  studyType: StudyType,
) => {
  const specificKey = `${status}_${studyType.toLowerCase()}`;

  let accepted = DropAcceptTypes[specificKey];
  if (!accepted) {
    accepted = DropAcceptTypes[status];
  }
  if (!accepted) {
    return [];
  }
  return accepted;
};

export const getDropAcceptTypes = (
  status: ParticipationStatus[],
  studyType: StudyType,
) => {
  return status.reduce((acc, s) => {
    return acc.concat(getDropAcceptTypesForSingleStatus(s, studyType));
  }, [] as string[]);
};

type Item = { type: string; id: string; participation: Participation };

export const useDropMethod = (
  status: ParticipationStatus | ParticipationStatus[],
  studyType: StudyType,
  options: {
    onDragToCancel?: (id: string) => any;
    onDragToBook?: (id: string) => any;
    onDragFromOFQ?: (id: string) => any;
  },
) => {
  const toast = useToast();
  const statusList = Array.isArray(status) ? status : [status];
  const requestBillingInformation = useRequestBillingInformation();

  const { mutate: decline } = useDeclineParticipation();
  const { mutateAsync: accept } = useAcceptParticipation();
  const { mutate: undoDecline } = useUndoDeclineParticipation();
  const { mutate: markAsParticipated } = useMarkAsParticipated();
  const { mutate: payParticipant } = usePayParticipant();
  const { mutate: report } = useReportParticipant();
  const { mutateAsync: createEvaluationFeedback } =
    useCreateDefaultEvaluationParticipation();

  const evaluateAndPayParticipant = useCallback(
    async (item: Item) => {
      await createEvaluationFeedback(item.participation);
      return payParticipant({ participation: item.participation });
    },
    [createEvaluationFeedback, payParticipant],
  );

  const callApi = useCallback(
    async (item: Item) => {
      focusManager.setFocused(false);
      const status = statusList.find((s) => {
        const types = getDropAcceptTypesForSingleStatus(s, studyType);
        return types.includes(item.type);
      });
      if (!status) {
        return;
      }
      if (ParticipationDragTypes[status] === item.type) {
        return { toInitial: true };
      }
      setTimeout(() => {
        focusManager.setFocused(undefined);
      }, 1000);

      switch (status) {
        case ParticipationStatus.APPLIED:
          return undoDecline(item.participation);

        case ParticipationStatus.ACCEPTED:
          return item.participation.status === ParticipationStatus.OUT_OF_QUOTA
            ? options.onDragFromOFQ && options.onDragFromOFQ(item.id)
            : accept(item.participation);

        case ParticipationStatus.DECLINED:
          return decline(item.participation);

        case ParticipationStatus.BOOKED:
          return options.onDragToBook && options.onDragToBook(item.id);

        case ParticipationStatus.PARTICIPATED:
          return markAsParticipated(item.participation);

        case ParticipationStatus.PAYMENT_REQUESTED:
          return evaluateAndPayParticipant(item);

        case ParticipationStatus.NO_SHOW:
          return report({
            participation: item.participation,
            reportReason: ParticipationReportReason.NO_SHOW,
          });

        case ParticipationStatus.CANCELED:
          return options.onDragToCancel && options.onDragToCancel(item.id);
        default:
          throw Error(`Invalid status ${status}`);
      }
    },
    [
      options,
      statusList,
      studyType,
      accept,
      decline,
      markAsParticipated,
      undoDecline,
      payParticipant,
      report,
    ],
  );

  return useCallback(
    async (item: Item) => {
      try {
        return await callApi(item);
      } catch (e) {
        if (
          e instanceof ApiFormattedException &&
          e.getCode() === 'INCOMPLETE_BILLING_INFO'
        ) {
          try {
            await requestBillingInformation();
            await callApi(item);
          } catch (err) {
            toast.exception(err);
            return;
          }
        } else {
          toast.exception(e);
        }
      }
    },
    [requestBillingInformation, callApi, toast],
  );
};
